import {
  LayoutTemplate,
  Megaphone,
  PanelsTopLeft,
  Plug,
  Rocket,
  Search,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import React from "react";
import { PageLayout } from "../components";
import ProductOverview from "../components/product/ProductOverview";
import { useTranslation } from "../contexts/LanguageContext";
import { openSiteBuilderSSO } from "../shared/siteBuilderSso";

/**
 * Página comercial do Virtus Site Builder (plataforma separada de landing pages com IA).
 * O acesso é por SSO com a sessão do SMM — ver `openSiteBuilderSSO()`.
 */
const SiteBuilder: React.FC = () => {
  const { t } = useTranslation();

  return (
    <PageLayout
      title={t("Virtus Site Builder")}
      subtitle={t(
        "Landing pages inteligentes, criadas com IA e sob medida para cada cliente, produto ou campanha.",
      )}
      containerSize="full"
      showContainer={true}
      padding="none"
      className="w-full px-2! py-2!"
    >
      <ProductOverview
        icon={PanelsTopLeft}
        productName={t("Virtus Site Builder")}
        tagline={t(
          "Crie landing pages e sites com IA em minutos, com design moderno, otimização para SEO e integração com suas campanhas, leads e CRM.",
        )}
        description={t(
          "Descreva a oferta, o público e o objetivo da campanha, e a IA monta a página completa, com textos, imagens e estrutura, pronta para publicar com domínio próprio e formulário de captura. O acesso é feito com o mesmo login do Social Manager, sem precisar entrar de novo.",
        )}
        ctaLabel={t("Abrir Virtus Site Builder")}
        onCta={openSiteBuilderSSO}
        benefitsTitle={t("Por que usar o Site Builder")}
        benefitsSubtitle={t("O que ele resolve nas suas campanhas")}
        benefits={[
          {
            icon: Sparkles,
            title: t("Crie com IA"),
            description: t(
              "Páginas completas com textos, imagens e estrutura em minutos.",
            ),
          },
          {
            icon: LayoutTemplate,
            title: t("Design profissional"),
            description: t(
              "Templates modernos, responsivos e prontos para converter.",
            ),
          },
          {
            icon: Search,
            title: t("Foco em resultados"),
            description: t(
              "Otimizado para SEO e integrado com suas campanhas.",
            ),
          },
        ]}
        featuresTitle={t("Principais recursos")}
        featuresSubtitle={t("O que você encontra dentro do Site Builder")}
        features={[
          {
            icon: Sparkles,
            title: t("Geração com IA"),
            description: t(
              "A página sai pronta a partir da descrição da oferta, sem esperar por design e desenvolvimento.",
            ),
          },
          {
            icon: Plug,
            title: t("Integração total"),
            description: t(
              "Conecte formulários, CRM, WhatsApp e as ferramentas que você já usa.",
            ),
          },
          {
            icon: ShieldCheck,
            title: t("Publicação segura"),
            description: t(
              "Hospedagem rápida, com domínio próprio e alta performance.",
            ),
          },
          {
            icon: Users,
            title: t("Captura de leads"),
            description: t(
              "Páginas focadas em conversão, com formulário de captura integrado.",
            ),
          },
          {
            icon: Rocket,
            title: t("Acesso único (SSO)"),
            description: t(
              "Entra direto com o login do Social Manager, sem precisar de outra senha.",
            ),
          },
        ]}
        flowTitle={t("Como funciona")}
        flowSubtitle={t("Da oferta à página no ar")}
        flow={[
          { icon: Megaphone, label: t("Descreva a oferta") },
          { icon: Sparkles, label: t("A IA cria a página") },
          { icon: Users, label: t("Publique e capte leads") },
        ]}
      />
    </PageLayout>
  );
};

export default SiteBuilder;
